/**
 * Safely extracts a value from an object using key or dot-notation path (e.g., "user.name", "account.score").
 */
export function getSortValue(item, columnKey) {
  if (!item || !columnKey) return null;
  if (columnKey in item) return item[columnKey];
  const keys = String(columnKey).split(".");
  let val = item;
  for (const k of keys) {
    if (val == null) return null;
    val = val[k];
  }
  return val;
}

/**
 * Checks if a value is considered empty (null, undefined, NaN, or whitespace-only string).
 */
export function isEmptyValue(val) {
  if (val === null || val === undefined) return true;
  if (typeof val === "number" && Number.isNaN(val)) return true;
  if (typeof val === "string" && val.trim() === "") return true;
  return false;
}

/**
 * Clean numeric parser: extracts numeric value from numbers, numeric strings, currency ("$1,250.50"), percentages ("25%"), etc.
 */
export function parseNumericValue(val) {
  if (typeof val === "number") return val;
  if (typeof val === "boolean") return val ? 1 : 0;
  if (typeof val === "string") {
    // Strip common formatting like $, %, commas
    const cleanStr = val.replace(/[$%,\s]/g, "");
    if (cleanStr !== "" && !Number.isNaN(Number(cleanStr))) {
      return parseFloat(cleanStr);
    }
  }
  return null;
}

/**
 * Collator for natural alphanumeric string sorting (e.g. "Item 2" comes before "Item 10", case-insensitive).
 */
const naturalCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
  ignorePunctuation: true,
});

/**
 * Industry-grade comparison function handling:
 * 1. Empty values (null, undefined, "") placed at the bottom regardless of direction
 * 2. Numbers & Numeric strings (currency, percentages, floats, ints)
 * 3. Dates (ISO strings, Date objects)
 * 4. Natural Alphanumeric Strings ("Item 2" < "Item 10", case-insensitive)
 * 5. Booleans
 */
export function compareValues(valA, valB, direction = "ascending") {
  const isDesc = direction === "descending";
  const emptyA = isEmptyValue(valA);
  const emptyB = isEmptyValue(valB);

  // Rule 1: Always push empty values to the bottom regardless of sort direction
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  // Rule 2: Compare Booleans
  if (typeof valA === "boolean" && typeof valB === "boolean") {
    const numA = valA ? 1 : 0;
    const numB = valB ? 1 : 0;
    return isDesc ? numB - numA : numA - numB;
  }

  // Rule 3: Compare Numbers or Numeric strings ($1,200, 15%, 10 vs 2)
  const numA = parseNumericValue(valA);
  const numB = parseNumericValue(valB);
  if (numA !== null && numB !== null) {
    return isDesc ? numB - numA : numA - numB;
  }

  // Rule 4: Compare Dates
  if (valA instanceof Date && valB instanceof Date) {
    const timeA = valA.getTime();
    const timeB = valB.getTime();
    return isDesc ? timeB - timeA : timeA - timeB;
  }

  // Rule 5: Compare Date Strings (ISO or valid date formats)
  if (typeof valA === "string" && typeof valB === "string") {
    const dateA = Date.parse(valA);
    const dateB = Date.parse(valB);
    if (
      !Number.isNaN(dateA) &&
      !Number.isNaN(dateB) &&
      valA.length >= 8 &&
      (valA.includes("-") || valA.includes("/")) &&
      (valB.includes("-") || valB.includes("/"))
    ) {
      return isDesc ? dateB - dateA : dateA - dateB;
    }
  }

  // Rule 6: Natural Alphanumeric String Comparison
  const strA = String(valA).trim();
  const strB = String(valB).trim();
  const cmp = naturalCollator.compare(strA, strB);
  return isDesc ? -cmp : cmp;
}

/**
 * Main sorting helper for array of objects.
 */
export function sortData(data = [], sortColumn = "", sortDirection = "ascending", getValueFn = null) {
  if (!Array.isArray(data) || data.length <= 1 || !sortColumn || !sortDirection) {
    return data;
  }

  return [...data].sort((itemA, itemB) => {
    const valA = getValueFn ? getValueFn(itemA, sortColumn) : getSortValue(itemA, sortColumn);
    const valB = getValueFn ? getValueFn(itemB, sortColumn) : getSortValue(itemB, sortColumn);
    return compareValues(valA, valB, sortDirection);
  });
}
